import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquare, MessageSquareOff } from "lucide-react";

export const CANCEL_REASONS = [
  { id: "client_cancelled", label: "Client cancelled" },
  { id: "client_rescheduled", label: "Client rescheduled" },
  { id: "client_unwell", label: "Client unwell" },
  { id: "clinic_cancelled", label: "Clinic cancelled" },
  { id: "mistake", label: "Booked by mistake", silent: true },
  { id: "duplicate", label: "Duplicate booking", silent: true },
  { id: "other", label: "Other" },
];

// Shared "why are we cancelling?" sheet. Mistake / duplicate never text the client.
export const CancelDialog = ({ open, onOpenChange, title = "Cancel booking", description, confirmLabel = "Cancel booking", onConfirm }) => {
  const [reason, setReason] = useState("client_cancelled");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason("client_cancelled"); setNote(""); setNotify(true); setBusy(false); } }, [open]);
  const silent = CANCEL_REASONS.find((r) => r.id === reason)?.silent;
  const willText = notify && !silent;
  const go = async () => {
    setBusy(true);
    try { await onConfirm({ reason, note, notify: willText }); onOpenChange(false); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cancel-dialog">
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Reason</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {CANCEL_REASONS.map((r) => (
                <button key={r.id} type="button" onClick={() => setReason(r.id)} data-testid={`cancel-reason-${r.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${reason === r.id ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{r.label}</button>
              ))}
            </div>
          </div>
          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="h-11 rounded-lg" data-testid="cancel-note" />
          <div className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${silent ? "border-border bg-muted/40" : "border-primary/30 bg-primary/5"}`} data-testid="cancel-notify-row">
            <span className="flex items-center gap-2 text-sm min-w-0">
              {willText ? <MessageSquare className="h-4 w-4 text-primary shrink-0" /> : <MessageSquareOff className="h-4 w-4 text-muted-foreground shrink-0" />}
              <span className="min-w-0">
                <span className="block font-medium">{silent ? "No text will be sent" : willText ? "Client gets a cancellation text" : "No text will be sent"}</span>
                <span className="block text-[11px] text-muted-foreground">{silent ? "Booked by mistake / duplicate — the client is never messaged." : "Turn off to cancel quietly."}</span>
              </span>
            </span>
            <Switch checked={willText} disabled={!!silent} onCheckedChange={setNotify} data-testid="cancel-notify-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-lg" onClick={() => onOpenChange(false)} data-testid="cancel-dialog-back">Keep booking</Button>
          <Button variant="destructive" className="rounded-lg" disabled={busy} onClick={go} data-testid="cancel-dialog-confirm">{busy ? "Cancelling…" : confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
